import { AnalyticsUtil, ANALYTICS_EVENTS } from '../utils/analytics.util';
import { router } from '../utils/router.util';
import { artemisService } from '../services/artemis.service';
import { ChallengeFormatter, calculateTotalPrize, campaignCache, getBenefitUnit } from '../utils/campaign.util';
import type { AAMedia } from '@akross/artemis-ads-sdk';
import { ROUTES } from '../constants/routes.constants';
import { logger } from '../utils/logger.util';
import { isSafariBrowser, openUrlInNewTab } from '../utils/browser.util';

interface RewardPageArgs {
  media: AAMedia;
}

interface AutoRedirectConfig {
  url: string;
  waitSeconds: number;
}

export class ArtemisRewardPage {
  private container: HTMLElement;
  private media!: AAMedia;
  private campaignId!: string;
  private requestId!: string;
  private mediaId!: string;
  private autoRedirectTimer: number | null = null;
  private autoRedirectConfig: AutoRedirectConfig | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /**
   * Serializa objeto removendo funções/métodos mas mantendo todas as propriedades
   */
  private static serializeObject(obj: any): any {
    // Usar JSON.parse/stringify para remover métodos e manter apenas dados
    // Isso preserva todas as propriedades do objeto, incluindo aninhadas
    return JSON.parse(JSON.stringify(obj, (_key, value) => {
      // Remover funções e métodos
      if (typeof value === 'function') {
        return undefined;
      }
      // Manter tudo mais (incluindo objetos, arrays, primitivos)
      return value;
    }));
  }

  /**
   * Método estático para abrir a página de sucesso
   * @param media - Objeto AAMedia obrigatório contendo todos os IDs necessários
   */
  static openPage(media: AAMedia): void {
    if (!media) {
      logger.error('[ArtemisRewardPage] Media é obrigatória');
      return;
    }

    // Serializar objeto completo da mídia (remove métodos, mantém todas propriedades)
    // Todos os IDs necessários (campaignId, requestId, trackingId, mediaId) já estão dentro do objeto media
    const serializableMedia = this.serializeObject(media);

    // Preparar argumentos
    const args: RewardPageArgs = {
      media: serializableMedia as AAMedia
    };

    // Salvar argumentos no sessionStorage para passar para a página
    sessionStorage.setItem('reward_page_args', JSON.stringify(args));

    // Navegar para a rota
    router.navigate(ROUTES.REWARD);
  }

  async render(): Promise<void> {
    // Ler argumentos do sessionStorage
    const argsJson = sessionStorage.getItem('reward_page_args');
    if (!argsJson) {
      this.renderError();
      return;
    }

    try {
      const args: RewardPageArgs = JSON.parse(argsJson);

      // Media é obrigatória
      if (!args.media) {
        this.renderError();
        return;
      }

      this.media = args.media;

      // Limpar argumentos após ler
      sessionStorage.removeItem('reward_page_args');
    } catch (error) {
      logger.error('[ArtemisRewardPage] Erro ao ler argumentos da página de sucesso:', error);
      this.renderError();
      return;
    }

    // Extrair todos os IDs diretamente do objeto media
    this.campaignId = this.media.campaignId || '';
    this.requestId = this.media.requestId || '';
    this.mediaId = this.media.uuid || '';

    // Extrair autoRedirectUrl (objeto com url e waitSeconds) se existir
    const raw = (this.media as any).success?.autoRedirectUrl;
    if (raw && typeof raw.url === 'string' && typeof raw.waitSeconds === 'number') {
      this.autoRedirectConfig = { url: raw.url, waitSeconds: raw.waitSeconds };
    } else {
      this.autoRedirectConfig = null;
    }

    // Validar que temos os IDs essenciais
    if (!this.campaignId || !this.mediaId) {
      logger.error('[ArtemisRewardPage] IDs essenciais ausentes:', {
        campaignId: this.campaignId,
        mediaId: this.mediaId
      });
      this.renderError();
      return;
    }

    // Enviar eventos de analytics (async, mas não aguardar)
    this.sendAnalyticsEvents().catch(error => {
      logger.error('[ArtemisRewardPage] Erro ao enviar eventos de analytics:', error);
    });

    // Renderizar conteúdo
    this.renderContent();
    this.attachEvents();

    // Iniciar timer de redirecionamento automático se necessário
    if (this.autoRedirectConfig) {
      this.startAutoRedirectTimer();
    }
  }

  private async sendAnalyticsEvents(): Promise<void> {
    // Evento de success_impression via SDK do Artemis
    await this.sendSuccessImpressionEvent();

    // Evento de success_impression (analytics genérico)
    AnalyticsUtil.track(ANALYTICS_EVENTS.SUCCESS_IMPRESSION, {
      campaignId: this.campaignId,
      requestId: this.requestId
    });

    // Evento de recompensa (coin_rewarded)
    AnalyticsUtil.trackWithArtemisIds(ANALYTICS_EVENTS.COIN_REWARDED, {
      campaignId: this.campaignId,
      mediaId: this.mediaId,
      requestId: this.requestId
    });
  }

  /**
   * Envia evento success_impression via SDK do Artemis
   */
  private async sendSuccessImpressionEvent(): Promise<void> {
    if (!this.campaignId || !this.requestId) {
      logger.warnWithContext('ArtemisRewardPage', 'Não é possível enviar success_impression: campaignId ou trackingId ausente');
      return;
    }

    // Enviar evento success_impression via serviço do Artemis
    await artemisService.sendEvent('success_impression', this.campaignId, this.requestId, this.mediaId);
  }

  private renderContent(): void {
    // Buscar campanha do cache para obter totalPrize
    const campaign = campaignCache.getCampaign(this.campaignId);
    const totalPrize = campaign ? calculateTotalPrize(campaign) : 0;
    const unit = campaign ? getBenefitUnit(campaign) || 'MB' : 'MB';
    const formattedPrize = ChallengeFormatter.formatPrize(totalPrize);

    this.container.innerHTML = `
      <div class="reward-page">
        <div class="reward-page-content">
          ${this.buildTimerIndicator()}
          <div class="reward-page-stack">
            <!-- Ícone de checkmark -->
            <div class="reward-page-icon-container">
              <img src="/reward-success.png" alt="Sucesso" class="reward-page-checkmark" />
            </div>

            <!-- Título e subtítulo -->
            <div class="reward-page-header">
              <h1 class="reward-page-title">Sucesso!</h1>
              <p class="reward-page-subtitle">Aproveite seu pacote de dados</p>
            </div>

            <!-- Seção de dados carregados -->
            <div class="reward-page-points-section">
              <div class="reward-page-points-row">
                <span class="reward-page-points-label">Dados carregados</span>
                <span class="reward-page-points-value">${formattedPrize} ${unit}</span>
              </div>
              <div class="reward-page-divider"></div>
            </div>

            <!-- Texto instrucional -->
            <div class="reward-page-instruction">
              <p>Seu pacote de dados será ativado em até 24h</p>
            </div>

            <!-- Botão Fechar -->
            <div class="reward-page-footer">
              <button class="reward-button reward-button-close" id="reward-close-btn">
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private buildTimerIndicator(): string {
    if (!this.autoRedirectConfig || this.autoRedirectConfig.waitSeconds === 0) {
      return '';
    }

    return `
      <div class="reward-timer-indicator" id="reward-timer">
        <svg class="reward-timer-circle" viewBox="0 0 36 36">
          <circle class="reward-timer-bg" cx="18" cy="18" r="16"></circle>
          <circle class="reward-timer-progress" cx="18" cy="18" r="16" id="timer-progress"></circle>
        </svg>
        <span class="reward-timer-text" id="timer-text">${this.autoRedirectConfig.waitSeconds}</span>
      </div>
    `;
  }


  private renderError(): void {
    this.container.innerHTML = `
      <div class="reward-page">
        <div class="reward-page-content">
          <div class="reward-page-error">
            <h2>Erro</h2>
            <p>Não foi possível carregar as informações de recompensa.</p>
            <button class="reward-button reward-button-close" id="reward-back-btn">
              Voltar
            </button>
          </div>
        </div>
      </div>
    `;
    this.attachEvents();
  }

  private attachEvents(): void {
    // Botão de fechar
    const closeButton = this.container.querySelector('#reward-close-btn');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.handleClose());
    }

    // Botão de voltar (se houver - para tela de erro)
    const backButton = this.container.querySelector('#reward-back-btn');
    if (backButton) {
      backButton.addEventListener('click', () => this.handleClose());
    }
  }

  private handleClose(): void {
    // Sempre redirecionar para home
    router.navigate(ROUTES.HOME);
  }


  private performExternalRedirect(url: string): void {
    artemisService.sendEvent('success-ext-redirect', this.campaignId, this.requestId, this.mediaId).catch(error => {
      logger.error('[ArtemisRewardPage] Erro ao enviar evento success-ext-redirect:', error);
    });

    // Safari bloqueia window.open fora de gestos do usuário (ex: setTimeout).
    // Nesses casos, navegamos na mesma aba. Em outros browsers abrimos nova aba.
    if (isSafariBrowser()) {
      window.location.href = url;
    } else {
      openUrlInNewTab(url);
      this.handleClose();
    }
  }

  private startAutoRedirectTimer(): void {
    if (!this.autoRedirectConfig) {
      return;
    }

    const { url, waitSeconds } = this.autoRedirectConfig;

    if (waitSeconds === 0) {
      this.performExternalRedirect(url);
      return;
    }

    const timerElement = this.container.querySelector('#reward-timer');
    const progressElement = this.container.querySelector('#timer-progress') as SVGCircleElement;
    const textElement = this.container.querySelector('#timer-text');

    if (!timerElement || !progressElement || !textElement) {
      return;
    }

    let seconds = waitSeconds;
    const circumference = 2 * Math.PI * 16; // raio = 16
    const totalSeconds = waitSeconds;

    const updateTimer = () => {
      seconds--;

      // Atualizar texto
      textElement.textContent = seconds.toString();

      // Atualizar progresso do círculo
      const progress = (totalSeconds - seconds) / totalSeconds;
      const offset = circumference * (1 - progress);
      progressElement.style.strokeDashoffset = offset.toString();

      if (seconds <= 0) {
        this.performExternalRedirect(url);
      } else {
        this.autoRedirectTimer = window.setTimeout(updateTimer, 1000);
      }
    };

    // Inicializar progresso
    progressElement.style.strokeDasharray = `${circumference} ${circumference}`;
    progressElement.style.strokeDashoffset = circumference.toString();

    // Iniciar timer
    this.autoRedirectTimer = window.setTimeout(updateTimer, 1000);
  }

  /**
   * Limpar recursos ao destruir a página
   */
  destroy(): void {
    if (this.autoRedirectTimer !== null) {
      clearTimeout(this.autoRedirectTimer);
      this.autoRedirectTimer = null;
    }
  }
}

